import { Embroidery, StateObj } from 'src/types'
import { darkenColor } from './color'

export const startInversion = (ctx: CanvasRenderingContext2D, canvasWidth: number) => {
  ctx.translate(canvasWidth, 0)
  ctx.scale(-1, 1)
}

export const undoInversion = (ctx: CanvasRenderingContext2D, canvasWidth: number) => {
  ctx.scale(-1, 1) // 元に戻す
  ctx.translate(-canvasWidth, 0)
}

// embroidery関係

const fontFamilySetter = (ctx: CanvasRenderingContext2D, selectedFontValue: string): void => {
  const fontSize = selectedFontValue === 'Brush Script MT' ? '44px' : '32px'
  // 非同期でフォントを反映させるための関数
  const fontLoader = () => {
    const oldEnglishFont = new FontFace(selectedFontValue, `url(/fontFamily/embroidery/${selectedFontValue}.ttf)`)
    oldEnglishFont.load().then((font) => {
      document.fonts.add(font) // DOMにfont情報を読み込ませるの必須
      ctx.font = `${fontSize} ${font.family}`
    })
  }
  switch (selectedFontValue) {
    case 'MariageD': {
      // オールドイングリッシュ
      fontLoader()
      break
    }
    case 'CloisterBlack': {
      // Yell story 特殊文字
      fontLoader()
      break
    }
    case 'Brush Script MT':
      ctx.font = `${fontSize} ${selectedFontValue}`
      break

    default:
      break
  }
}

const liningEmbroidery = (ctx: CanvasRenderingContext2D, embroidery: Embroidery, x: number, y: number, fontSizeValue?: string): void => {
  fontFamilySetter(ctx, embroidery.typeFace.value)
  // 以下の font 再宣言必須
  const fontSize = fontSizeValue || embroidery.typeFace.value === 'Brush Script MT' ? '44px' : '32px'
  ctx.font = `${fontSize} ${embroidery.typeFace.value}`
  ctx.textAlign = 'center'
  ctx.lineWidth = 0.8
  ctx.fillStyle = embroidery.color.color // '#383838'
  // 手入れ口部分
  ctx.beginPath()
  // 影カラー
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowColor = embroidery?.shadowColor?.color
    ctx.shadowOffsetX = 3
    ctx.shadowOffsetY = 3
    ctx.shadowBlur = 3
  }
  // フチカラー
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 3.0
    ctx.strokeStyle = embroidery.edgeColor.color
  }
  ctx.strokeText(embroidery.content, 270 + x, 500 + y)
  ctx.fillText(embroidery.content, 270 + x, 500 + y)
  ctx.closePath()
  // 影カラーリセット
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
    ctx.shadowBlur = 0
  }
  // フチカラーリセット
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 0.8
    ctx.strokeStyle = '#282828'
  }
  ctx.textAlign = 'start'
}

const liningEmbroideryObjectLeftThrow = {
  first: {
    x: 210,
    y: -75,
    position: 'leatherLiningFirst'
  },
  second: {
    x: 215,
    y: -35,
    position: 'leatherLiningSecond'
  }
}

export const liningEmbroideryDrawerLeftThrow = (
  ctx: CanvasRenderingContext2D,
  embroideries: Embroidery[],
  step: 'first' | 'second',
  fontSize?: string
): void => {
  const isEmbroideryOfLining = embroideries?.some((x) => x.position.value === liningEmbroideryObjectLeftThrow[step].position)
  if (isEmbroideryOfLining) {
    liningEmbroidery(
      ctx,
      embroideries.find((embroidery) => embroidery.position.value === liningEmbroideryObjectLeftThrow[step].position),
      liningEmbroideryObjectLeftThrow[step].x,
      liningEmbroideryObjectLeftThrow[step].y,
      fontSize
    )
  }
}

export const notThumbEmbroideryLeftThrow = (
  ctx: CanvasRenderingContext2D,
  embroidery: Embroidery,
  x: number,
  y: number,
  numerator?: number
): void => {
  fontFamilySetter(ctx, embroidery.typeFace.value)
  // 以下の font 再宣言必須
  const fontSize = embroidery.typeFace.value === 'Brush Script MT' ? '44px' : '26px'
  ctx.font = `${fontSize} ${embroidery.typeFace.value}`
  ctx.textAlign = 'start'
  ctx.lineWidth = 0.8
  ctx.fillStyle = embroidery.color.color // '#383838'
  // 手入れ口部分
  ctx.save()
  ctx.rotate(((-85 + (numerator || 0)) * Math.PI) / 180)
  ctx.beginPath()

  // 影カラー
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowColor = embroidery?.shadowColor?.color
    ctx.shadowOffsetX = 3
    ctx.shadowOffsetY = 3
    ctx.shadowBlur = 3
  }
  // フチカラー
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 2.5
    ctx.strokeStyle = embroidery.edgeColor.color
  }
  ctx.strokeText(embroidery.content, -300 + x, 710 + y)
  ctx.fillText(embroidery.content, -300 + x, 710 + y)
  ctx.closePath()
  // 影カラーリセット
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
    ctx.shadowBlur = 0
  }
  // フチカラーリセット
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 0.8
    ctx.strokeStyle = '#282828'
  }
  ctx.restore()
}

export const firstMittChildFingerEmbroidery = (ctx: CanvasRenderingContext2D, embroidery: Embroidery, x: number, y: number): void => {
  fontFamilySetter(ctx, embroidery.typeFace.value)
  const typeFace = embroidery.typeFace.value
  const fontSize = typeFace === 'Brush Script MT' ? '30px' : typeFace === 'Zapfino, cursive' ? '14px' : '22px'
  ctx.font = `${fontSize} ${embroidery.typeFace.value}`
  ctx.textAlign = 'start'
  ctx.lineWidth = 0.8
  ctx.fillStyle = embroidery.color.color // '#383838'
  // 手入れ口部分
  ctx.save()
  ctx.rotate((-10 * Math.PI) / 180)
  ctx.beginPath()

  // 影カラー
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowColor = embroidery?.shadowColor?.color
    ctx.shadowOffsetX = 3
    ctx.shadowOffsetY = 3
    ctx.shadowBlur = 3
  }
  // フチカラー
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 2.5
    ctx.strokeStyle = embroidery.edgeColor.color
  }
  ctx.strokeText(embroidery.content, 265 + x, -160 + y)
  ctx.fillText(embroidery.content, 265 + x, -160 + y)
  ctx.closePath()
  // 影カラーリセット
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
    ctx.shadowBlur = 0
  }
  // フチカラーリセット
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 0.8
    ctx.strokeStyle = '#282828'
  }
  ctx.restore()
}

export const firstMittChildFingerEmbroideryLeftThrow = (
  ctx: CanvasRenderingContext2D,
  embroidery: Embroidery,
  x: number,
  y: number,
  numerator?: number
): void => {
  fontFamilySetter(ctx, embroidery.typeFace.value)
  // 以下の font 再宣言必須
  const fontSize = embroidery.typeFace.value === 'Brush Script MT' ? '25px' : '18px'
  ctx.font = `${fontSize} ${embroidery.typeFace.value}`
  ctx.textAlign = 'start'
  ctx.lineWidth = 0.8
  ctx.fillStyle = embroidery.color.color // '#383838'
  // 手入れ口部分
  ctx.save()
  ctx.rotate(((10 + (numerator || 0)) * Math.PI) / 180)
  ctx.beginPath()

  // 影カラー
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowColor = embroidery?.shadowColor?.color
    ctx.shadowOffsetX = 3
    ctx.shadowOffsetY = 3
    ctx.shadowBlur = 3
  }
  // フチカラー
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 2.5
    ctx.strokeStyle = embroidery.edgeColor.color
  }
  ctx.strokeText(embroidery.content, -300 + x, 710 + y)
  ctx.fillText(embroidery.content, -300 + x, 710 + y)
  ctx.closePath()
  // 影カラーリセット
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
    ctx.shadowBlur = 0
  }
  // フチカラーリセット
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 0.8
    ctx.strokeStyle = '#282828'
  }
  ctx.restore()
}

const nonThumbEmbroideryObjectLeftThrow = {
  middleFinger: {
    x: 0,
    y: 0,
    numerator: 1
  },
  ringFinger: {
    x: 0,
    y: 0,
    numerator: 1
  },
  childFinger: {
    x: 0,
    y: 0,
    numerator: 1
  }
}
export const nonThumbEmbroideryDrawerLeftThrow = (
  ctx: CanvasRenderingContext2D,
  fingerType: 'middleFinger' | 'ringFinger' | 'childFinger',
  embroideries: Embroidery[]
): void => {
  const isEmbroideryDraw = embroideries?.some((x) => x.position.value === fingerType)
  if (isEmbroideryDraw) {
    notThumbEmbroideryLeftThrow(
      ctx,
      embroideries.find((embroidery) => embroidery.position.value === fingerType),
      nonThumbEmbroideryObjectLeftThrow[fingerType].x,
      nonThumbEmbroideryObjectLeftThrow[fingerType].y,
      nonThumbEmbroideryObjectLeftThrow[fingerType].numerator
    )
  }
}

const thumbEmbroideryLeftThrow = (ctx: CanvasRenderingContext2D, embroidery: Embroidery, x: number, y: number, numerator?: number): void => {
  ctx.lineWidth = 0.8
  ctx.fillStyle = embroidery.color.color // '#383838'
  ctx.strokeStyle = embroidery.color.color
  ctx.textAlign = 'start'
  ctx.save()
  ctx.rotate(((50 + (numerator || 0)) * Math.PI) / 180)
  ctx.beginPath()
  // 影カラー
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowColor = embroidery?.shadowColor?.color
    ctx.shadowOffsetX = 3
    ctx.shadowOffsetY = 3
    ctx.shadowBlur = 3
  }
  // フチカラー
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 2.5
    ctx.strokeStyle = embroidery.edgeColor.color
  }
  fontFamilySetter(ctx, embroidery.typeFace.value)
  // 以下の font 再宣言必須
  const typeFace = embroidery.typeFace.value
  const fontSize = typeFace === 'Brush Script MT' ? '30px' : typeFace === 'Zapfino, cursive' ? '14px' : '22px'
  const textCount = embroidery.content.length
  const width = textCount > 7 ? 380 : textCount > 4 ? 480 : 500
  ctx.font = `${fontSize} ${embroidery.typeFace.value}`
  ctx.strokeText(embroidery.content, width + x, 70 + y)
  ctx.fillText(embroidery.content, width + x, 70 + y)
  ctx.closePath()
  // 影カラーリセット
  if (embroidery?.shadowColor?.value !== 'none') {
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 0
    ctx.shadowBlur = 0
  }
  // フチカラーリセット
  if (embroidery?.edgeColor?.value !== 'none') {
    ctx.lineWidth = 0.8
    ctx.strokeStyle = '#282828'
  }
  ctx.restore()
}
const positionCompare = (e: Embroidery) => (target: string) => e.position.value === target
export const thumbEmbroideryDrawerLeftThrow = (ctx: CanvasRenderingContext2D, embroideries: Embroidery[], backStyle: StateObj): void => {
  const matcher = (e: Embroidery) => positionCompare(e)('thumbFinger')
  const isEmbroideryDraw = embroideries?.some(matcher)
  if (!isEmbroideryDraw) return
  switch (backStyle?.value) {
    case 'dWave':
      thumbEmbroideryLeftThrow(ctx, embroideries.find(matcher), 35, -10, 0)
      break
    default:
      thumbEmbroideryLeftThrow(ctx, embroideries.find(matcher), -50, -3, 0)
      break
  }
}

export const thumbRightEmbroideryDrawerLeftThrow = (ctx: CanvasRenderingContext2D, embroideries: Embroidery[]): void => {
  const matcher = (e: Embroidery) => positionCompare(e)('thumbFingerRight')
  const isEmbroideryDraw = embroideries?.some(matcher)
  if (!isEmbroideryDraw) return
  thumbEmbroideryLeftThrow(ctx, embroideries.find(matcher), -90, 10, 0)
}

export const thumbLeftEmbroideryDrawerLeftThrow = (ctx: CanvasRenderingContext2D, embroideries: Embroidery[]): void => {
  const matcher = (e: Embroidery) => positionCompare(e)('thumbFingerLeft')
  const isEmbroideryDraw = embroideries?.some(matcher)
  if (!isEmbroideryDraw) return
  thumbEmbroideryLeftThrow(ctx, embroideries.find(matcher), 45, 420, -43)
}

export const firstBackThumbRightEmbroideryDrawerLeftThrow = (ctx: CanvasRenderingContext2D, embroideries: Embroidery[]): void => {
  const matcher = (e: Embroidery) => positionCompare(e)('thumbFingerRight')
  const isEmbroideryDraw = embroideries?.some(matcher)
  if (!isEmbroideryDraw) return
  thumbEmbroideryLeftThrow(ctx, embroideries.find(matcher), -60, 10, 0)
}

export const firstBackThumbLeftEmbroideryDrawerLeftThrow = (ctx: CanvasRenderingContext2D, embroideries: Embroidery[]): void => {
  const matcher = (e: Embroidery) => positionCompare(e)('thumbFingerLeft')
  const isEmbroideryDraw = embroideries?.some(matcher)
  if (!isEmbroideryDraw) return
  thumbEmbroideryLeftThrow(ctx, embroideries.find(matcher), 20, 320, -35)
}

export const thumbEmbroideryCatcherDrawerLeftThrow = (ctx: CanvasRenderingContext2D, embroideries: Embroidery[]): void => {
  const matcher = (e: Embroidery) => positionCompare(e)('thumbFinger')
  const isEmbroideryDraw = embroideries?.some(matcher)
  if (!isEmbroideryDraw) return
  thumbEmbroideryLeftThrow(ctx, embroideries.find(matcher), 60, 40, -5)
}

export const generateLiningsGradient = (
  ctx: CanvasRenderingContext2D,
  color: string,
  coordinates: { x0: number; y0: number; x1: number; y1: number },
  rate = 0.25
) => {
  const { x0, y0, x1, y1 } = coordinates
  /**
   * x0: グラデーションの開始位置の x 座標
   * y0: グラデーションの開始位置の y 座標
   * x1: グラデーションの終了位置の x 座標
   * y1: グラデーションの終了位置の y 座標
   */
  const gradient = ctx.createLinearGradient(x0, y0, x1, y1)

  // 'color' が undefinedになるケースがあるので、初期値を設定
  const initializedColor = color ? color : '#fff'
  gradient.addColorStop(0, initializedColor)
  gradient.addColorStop(0, darkenColor(initializedColor, rate))
  gradient.addColorStop(1, initializedColor)
  return gradient
}
